import { useCallback, useEffect, useState } from "react";
import {
  addBan,
  ApiError,
  type BanRecord,
  clearAdminToken,
  deleteBan,
  getAdminToken,
  listBans,
  setAdminToken,
} from "./api.ts";

export function App() {
  const [token, setToken] = useState<string | null>(getAdminToken());
  if (!token)
    return (
      <TokenGate
        onSubmit={(t) => {
          setAdminToken(t);
          setToken(t);
        }}
      />
    );
  return (
    <BansPanel
      onSignOut={() => {
        clearAdminToken();
        setToken(null);
      }}
    />
  );
}

function TokenGate({ onSubmit }: { onSubmit: (token: string) => void }) {
  const [value, setValue] = useState("");
  return (
    <div className="container">
      <h1>Podcast Admin</h1>
      <p className="muted">
        管理者トークン (<code>X-Admin-Token</code>) を入力してください。 トークンは localStorage
        に保存されます。
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const v = value.trim();
          if (v) onSubmit(v);
        }}
      >
        <label htmlFor="token-input">Admin Token</label>
        <input
          id="token-input"
          type="password"
          autoComplete="off"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
          }}
        />
        <div style={{ marginTop: 12 }}>
          <button type="submit" disabled={!value.trim()}>
            続行
          </button>
        </div>
      </form>
    </div>
  );
}

type InfoTone = "success" | "warning";

function BansPanel({ onSignOut }: { onSignOut: () => void }) {
  const [bans, setBans] = useState<BanRecord[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<{ message: string; tone: InfoTone } | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listBans();
      setBans(res.bans);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // Token rejected — bounce to gate.
        onSignOut();
        return;
      }
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [onSignOut]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <div className="container">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1>BAN 管理</h1>
        <button className="secondary" onClick={onSignOut}>
          トークンを破棄
        </button>
      </div>

      {error && <div className="banner error">{error}</div>}
      {info && <div className={`banner ${info.tone}`}>{info.message}</div>}

      <h2>新規 BAN 追加</h2>
      <AddBanForm
        onAdded={async (message, tone) => {
          setInfo({ message, tone });
          setError(null);
          await refresh();
        }}
        onError={(msg) => {
          setError(msg);
          setInfo(null);
        }}
      />

      <h2>登録済み BAN ({bans?.length ?? "?"})</h2>
      <p className="muted">
        v=1 は SHA-256 のみ、v=2 は HMAC-SHA256(pepper) のハッシュ。 1 電話番号につき 2 行 INSERT
        されます (移行期間の二重化)。
      </p>
      {loading && <p>読み込み中…</p>}
      {bans?.length === 0 && <p>BAN は登録されていません。</p>}
      {bans && bans.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>phone_number_hash</th>
              <th style={{ width: 40 }}>v</th>
              <th>reason</th>
              <th>banned_at</th>
              <th style={{ width: 80 }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {bans.map((b) => (
              <tr key={`${b.phoneNumberHash}-${String(b.phoneNumberHashV)}`}>
                <td>
                  <code>{b.phoneNumberHash}</code>
                </td>
                <td>{b.phoneNumberHashV}</td>
                <td>{b.reason}</td>
                <td className="muted">{b.bannedAt}</td>
                <td>
                  <button
                    className="danger"
                    onClick={() => {
                      if (
                        !confirm(
                          `この行 (v=${String(b.phoneNumberHashV)}) を削除します。よろしいですか?`,
                        )
                      ) {
                        return;
                      }
                      void (async () => {
                        try {
                          await deleteBan(b.phoneNumberHash, b.phoneNumberHashV);
                          setInfo({
                            message: `v=${String(b.phoneNumberHashV)} の BAN を削除しました`,
                            tone: "success",
                          });
                          setError(null);
                          await refresh();
                        } catch (err) {
                          setError(err instanceof Error ? err.message : String(err));
                          setInfo(null);
                        }
                      })();
                    }}
                  >
                    削除
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function AddBanForm({
  onAdded,
  onError,
}: {
  onAdded: (message: string, tone: InfoTone) => void | Promise<void>;
  onError: (message: string) => void;
}) {
  const [phone, setPhone] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const p = phone.trim();
        const r = reason.trim();
        if (!p || !r) return;
        setBusy(true);
        void (async () => {
          try {
            const result = await addBan(p, r);
            setPhone("");
            setReason("");
            // Three possible outcomes per backend insertDualBan semantics:
            //  - both inserted -> fresh BAN (green)
            //  - neither inserted -> already banned in both versions (yellow)
            //  - exactly one inserted -> migration gap closer (yellow)
            if (result.insertedV1 && result.insertedV2) {
              await onAdded("BAN を追加しました (v=1 + v=2 を新規 INSERT)", "success");
            } else if (!result.insertedV1 && !result.insertedV2) {
              await onAdded("すでに登録済の BAN です (v=1, v=2 とも既存)", "warning");
            } else if (result.insertedV2) {
              await onAdded(
                "v=2 を追加しました (v=1 は既存の legacy 行を保持、移行完了)",
                "warning",
              );
            } else {
              await onAdded("v=1 を追加しました (v=2 は既存行を保持)", "warning");
            }
          } catch (err) {
            onError(err instanceof Error ? err.message : String(err));
          } finally {
            setBusy(false);
          }
        })();
      }}
    >
      <div className="row">
        <div>
          <label htmlFor="phone">電話番号 (E.164, 例: +819012345678)</label>
          <input
            id="phone"
            type="tel"
            placeholder="+819012345678"
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value);
            }}
            autoComplete="off"
          />
        </div>
        <div>
          <label htmlFor="reason">理由 (1-500 文字)</label>
          <input
            id="reason"
            type="text"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
            }}
          />
        </div>
        <button type="submit" disabled={busy || !phone.trim() || !reason.trim()}>
          {busy ? "追加中…" : "BAN を追加"}
        </button>
      </div>
    </form>
  );
}
